import { RiEditLine, RiUserAddLine, RiSearchLine, RiCloseLine } from '@remixicon/react';
import { initials, formatMemberStatus } from '../utils/format';
import { ROLES } from '@/lib/permissions';

// Roles that a broker admin can filter by. Matches the Add Member drawer's
// role picker (TEAM_ROLES). Kept as an array so we don't depend on the
// order of ROLES object keys.
const ROLE_FILTER_OPTIONS = [
  { value: ROLES.ADMIN,              label: 'Admin' },
  { value: ROLES.BROKER,             label: 'Broker' },
  { value: ROLES.JUNIOR_BROKER,      label: 'Junior Broker' },
  { value: ROLES.FINANCE,            label: 'Finance' },
  { value: ROLES.FLIGHT_COORDINATOR, label: 'Flight Coordinator' },
  { value: ROLES.COMPLIANCE_QA,      label: 'Compliance / QA' },
];

// Status filter options — only enums a team member can actually land in:
//   ACTIVE / INACTIVE / ARCHIVED
// Owners are created directly ACTIVE by super-admin, invited members land at
// ACTIVE via TeamRegistration, so NEW / ONBOARDING_STARTED / ONBOARDING_COMPLETED
// are all unreachable in practice for org members. SUSPENDED is a
// support-only state the FE reads but never writes. All four are omitted so
// the dropdown doesn't advertise options that always return zero rows.
// Labels reuse formatMemberStatus so filter dropdown and row badge agree.
const STATUS_FILTER_OPTIONS = [
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
].map((v) => ({ value: v, label: formatMemberStatus(v).label }));

const AVATAR_COLORS = [
  'bg-amber-100 text-amber-900',
  'bg-blue-100 text-blue-900',
  'bg-purple-100 text-purple-900',
  'bg-rose-100 text-rose-900',
  'bg-emerald-100 text-emerald-900',
  'bg-indigo-100 text-indigo-900',
];

const avatarColorFor = (key = '') => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatRole = (role) => {
  if (!role) return '';
  const map = {
    admin: 'Admin',
    ADMIN: 'Admin',
    broker: 'Broker',
    junior_broker: 'Junior Broker',
    finance: 'Finance / Accounting',
  };
  return map[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Turn the persisted preferences.user_types array (e.g. ["broker"],
// ["operator"], or both) into a display string. Used only for the
// Owner row so the "I am a…" selection from signup surfaces
// alongside the Owner label.
const formatUserTypes = (types) => {
  if (!Array.isArray(types) || types.length === 0) return '';
  return types.map(formatRole).filter(Boolean).join(', ');
};

const TeamMemberRow = ({ member, isYou, rowIsOwner, viewerIsOwner, onEdit }) => {
  const first = member.first_name || '';
  const last = member.last_name || '';
  const fullName = [first, last].filter(Boolean).join(' ') || member.email || 'Member';
  const colorClass = avatarColorFor(member.id || member.email || fullName);
  // Status chip lets an admin scan at a glance who's Pending Activation /
  // Deactivated without opening the Edit drawer.
  const statusBadge = member.status ? formatMemberStatus(member.status) : null;
  // Edit-button visibility:
  //  - Owner row → only the owner themselves can open it. (Hidden for admins
  //    so they can't change the owner's role.)
  //  - Admin row → only the owner can edit (so the owner can demote admins).
  //  - Anything else → editable as before.
  const rowIsAdmin = member.role === 'admin' || member.role === 'ADMIN';
  const canEditRow = rowIsOwner ? viewerIsOwner : (rowIsAdmin ? viewerIsOwner : true);
  // Show "Owner" instead of the raw role label for the org owner.
  // When the owner's preferences.user_types was persisted at signup
  // (from the "I am a…" step), append it in parens — "Owner (Broker)"
  // — so viewers can see whether the owner operates as broker,
  // operator, or both. Falls back to plain "Owner" when null / empty.
  const ownerUserTypes = rowIsOwner ? formatUserTypes(member.preferences?.user_types) : '';
  const roleLabel = rowIsOwner
    ? (ownerUserTypes ? `Owner (${ownerUserTypes})` : 'Owner')
    : formatRole(member.role);
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${colorClass}`}
      >
        {initials(first, last) || (member.email || 'U').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {fullName}
          {isYou && <span className="font-normal text-gray-500"> (You)</span>}
        </p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{roleLabel}</p>
      </div>
      {statusBadge && (
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${statusBadge.className}`}>
          {statusBadge.label}
        </span>
      )}
      {canEditRow && (
        <button
          type="button"
          onClick={() => onEdit?.(member)}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={`Edit ${fullName}`}
        >
          <RiEditLine className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const TeamMembersPanel = ({
  members = [],
  currentUserId,
  currentUserprofileId,
  ownedBy,
  onEditMember,
  onAddMember,
  filters = { email: '', role: '', status: '' },
  onFilterChange = () => {},
}) => {
  const viewerIsOwner = !!ownedBy && currentUserprofileId === ownedBy;
  // Email search is debounced upstream (in AccountSettings via
  // debouncedTeamEmail) but the input itself has to feel responsive, so
  // we shadow the URL-persisted value with local state that flushes up
  // as the user types.
  const emailValue = filters.email || '';
  const roleValue = filters.role || '';
  const statusValue = filters.status || '';
  const hasFilters = !!(emailValue || roleValue || statusValue);

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] bg-white/70 backdrop-blur-sm rounded-[2.5rem] overflow-hidden">
      {/* Scrollable list area — the only part that scrolls. */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        <p className="text-sm text-gray-700 px-1">
          Invite team members to work with and assign member permissions.
        </p>

        {/* Filter bar — email search, role & status selects, clear link. */}
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <RiSearchLine className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={emailValue}
              onChange={(e) => onFilterChange({ email: e.target.value })}
              placeholder="Search by email…"
              className="w-full pl-9 pr-9 py-2 text-sm rounded-full bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
            {emailValue && (
              <button
                type="button"
                onClick={() => onFilterChange({ email: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full"
                aria-label="Clear search"
              >
                <RiCloseLine className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={roleValue}
            onChange={(e) => onFilterChange({ role: e.target.value })}
            className="text-sm rounded-full bg-white border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
          >
            <option value="">All roles</option>
            {ROLE_FILTER_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <select
            value={statusValue}
            onChange={(e) => onFilterChange({ status: e.target.value })}
            className="text-sm rounded-full bg-white border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
          >
            <option value="">All statuses</option>
            {STATUS_FILTER_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {hasFilters && (
            <button
              type="button"
              onClick={() => onFilterChange({ email: '', role: '', status: '' })}
              className="text-xs text-primary-700 hover:text-primary-800 hover:underline whitespace-nowrap px-2"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl divide-y divide-gray-100">
          {members.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              {hasFilters ? 'No members match these filters.' : 'No team members yet.'}
            </div>
          ) : (
            members.map((m) => (
              <TeamMemberRow
                key={m.id || m.email}
                member={m}
                isYou={m.id === currentUserId}
                rowIsOwner={!!ownedBy && m.userprofile_id === ownedBy}
                viewerIsOwner={viewerIsOwner}
                onEdit={onEditMember}
              />
            ))
          )}
        </div>
      </div>

      {/* Sticky footer — Add member stays pinned at the bottom of the panel. */}
      <div className="shrink-0 px-4 md:px-6 pb-4 pt-2 bg-white/70 backdrop-blur-sm">
        <button
          type="button"
          onClick={onAddMember}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-indigo-50 text-sm font-medium text-primary-700 hover:bg-indigo-100 transition-colors"
        >
          <RiUserAddLine className="w-4 h-4" />
          Add member
        </button>
      </div>
    </div>
  );
};

export default TeamMembersPanel;
